import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslocoService, translate } from '@jsverse/transloco';

import { AppService } from 'app/app.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { SimpleDialogButtonColor } from '../../../components/simple-dialog.component';
import { HasTitle } from '../../../has-title';
import { AuthService } from '../../../providers/bridge-api/auth.service';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from '../../../providers/semantria-api/configuration.service';
import { CollectionService } from '../../../providers/storage-api/collection.service';
import { AlertService } from '../../../services/alert.service';
import { DialogService } from '../../../services/dialog.service';
import { SystemService } from '../../../services/system.service';

import { CloneConfigurationDialogOptions } from '../components/clone-configuration-dialog/clone-configuration-dialog.component';
import { configNameValidatorFactoryAsync } from '../configuration-utils';

export abstract class BaseConfigurationComponent extends HasTitle {
  protected session;

  protected configurations: SemantriaConfiguration[];
  protected autoAnalysisConfigs: string[] = [];

  showCloneConfigurationDialog = false;
  cloneConfigData: CloneConfigurationDialogOptions;
  groupId: string;
  isReadOnly: boolean;

  uploadValidator = (fileUpload, importedItem) => {
    if (!importedItem.language_id || !importedItem.language_name) {
      return {
        isValid: false,
        message: translate('configurations.details.import-invalid', {
          fileName: fileUpload.file.name,
        }),
      };
    }

    if (importedItem.layer) {
      return {
        isValid: false,
        message: translate('configurations.details.import-standard-from-layered'),
      };
    }

    return {
      isValid: true,
    };
  };

  protected constructor(
    protected titleService: Title,
    protected appService: AppService,
    protected authService: AuthService,
    protected systemService: SystemService,
    protected dialogService: DialogService,
    protected alertService: AlertService,
    protected configService: ConfigurationService,
    protected groupService: GroupService,
    protected collectionService: CollectionService,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected translocoService: TranslocoService
  ) {
    super(titleService);
    this.collectionService
      .getAutoAnalysisConfigs()
      .then((res) => (this.autoAnalysisConfigs = res.items));
  }

  async onInit() {
    const url = this.activatedRoute.snapshot;
    this.groupId = url.paramMap.get('groupId');
    this.session = await this.authService.getSession();
    this.isReadOnly = this.authService.isReadOnly;
  }

  canDeleteConfiguration(config) {
    return (
      !this.isReadOnly &&
      !this.autoAnalysisConfigs.includes(config.id) &&
      !config.removed &&
      (!config.config_routes || !config.config_routes.length)
    );
  }

  canCloneConfiguration(config) {
    return (
      !this.isReadOnly &&
      !config.removed &&
      this.session &&
      this.authService.canCloneConfiguration(this.session, config.id)
    );
  }

  canDownloadConfiguration(config) {
    return !config.removed;
  }

  deleteConfigurationTooltip(config) {
    const translations = this.translocoService.translateObject(
      'configurations.details.tooltip-delete',
      {
        'part-of-route': {
          routes: config.config_routes?.map((e) => e.config_route_name).join(', '),
        },
      }
    );

    if (this.autoAnalysisConfigs.includes(config.id)) {
      return translations['auto-analysis'];
    } else if (config.removed) {
      return translations['deleted'];
    } else if (config.config_routes && config.config_routes.length) {
      return translations['part-of-route'];
    }
    return translations['deletable'];
  }

  downloadConfigurationTooltip(config) {
    const translations = this.translocoService.translateObject(
      'configurations.details.tooltip-download'
    );

    if (config.removed) {
      return translations['deleted'];
    }
    return translations['downloadable'];
  }

  cloneConfigurationTooltip(config) {
    const translations = this.translocoService.translateObject(
      'configurations.details.tooltip-clone'
    );

    if (config.removed) {
      return translations['deleted'];
    }
    if (this.isReadOnly) {
      return translations['readonly'];
    }
    return translations['cloneable'];
  }

  importConfigurationTooltip(): string {
    const translations = this.translocoService.translateObject(
      'configurations.details.tooltip-import'
    );

    if (this.isReadOnly) {
      return translations['readonly'];
    }
    return translations['importable'];
  }

  downloadTaxonomyTooltip(config): string {
    const translations = this.translocoService.translateObject(
      'configurations.details.tooltip-download-taxonomy'
    );

    if (config.removed) {
      return translations['deleted'];
    }
    return translations['downloadable'];
  }

  public async onDeleteConfiguration(config: SemantriaConfiguration) {
    const translations = this.translocoService.translateObject(
      'configurations.details.delete-modal',
      {
        'delete-success': {
          configuration: config.name,
        },
        'delete-failed': {
          configuration: config.name,
        },
      }
    );

    this.dialogService
      .showWarningPopup({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm-button'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.beforeConfigurationDeleted(config);

          this.configService
            .deleteConfiguration(config.id)
            .then(() => {
              config.removed = true;

              this.alertService.showToast(translations['delete-success']);
              this.router.navigate(['/groups', this.groupId, 'configurations']);
            })
            .catch((err) => {
              this.alertService.showErrorToast(translations['delete-failed'], err);
            });
        }
      });
  }

  onDownloadConfiguration(config: SemantriaConfiguration) {
    this.configService.exportConfiguration(config.id).then((res) => {
      const filename = config.name.replace(new RegExp('\\s', 'g'), '-') + '-export.json';

      const file = new File([JSON.stringify(res, null, '  ')], filename, {
        type: 'text/plain; charset=utf-8',
      });

      this.systemService.saveFile(file);
    });
  }

  async onCloneConfiguration(config: SemantriaConfiguration) {
    this.showCloneConfigurationDialog = true;

    this.cloneConfigData = {
      configuration: config,
      nameValidator: configNameValidatorFactoryAsync(this.configService, { ...config }, [config]),
    };
  }

  async onCloseCloneConfigurationDialog(res: SemantriaConfiguration) {
    const config = this.cloneConfigData.configuration;
    this.showCloneConfigurationDialog = false;
    if (res) {
      const groupId = this.groupService.getSelectedGroupId();
      try {
        const clone = await this.configService.createConfiguration(
          { ...res, config_routes: [] },
          groupId
        );
        this.alertService.showToast(
          this.translocoService.translate('configurations.details.clone-success', {
            source: config.name,
            target: clone.name,
          })
        );
        await this.groupService.refreshAndSelectActiveGroup(groupId, true);
        this.afterConfigurationCloned(clone);
      } catch (err) {
        this.alertService.showErrorToast(
          this.translocoService.translate('configurations.details.clone-failed', {
            source: res.name,
          }),
          err
        );
      }
    }
  }

  protected abstract beforeConfigurationDeleted(config);

  protected abstract afterConfigurationCloned(clone);

  protected abstract afterConfigurationMoved(config);
}
